import { useEffect, useMemo, useState } from 'react'
import { useEquipmentScope } from '../equipmentScope'
import { AlertTriangle, Check, FileText, Play } from 'lucide-react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import {
  eventMeta,
  TASK_CLASS_META,
  type MaintenanceTask,
} from '@/types/maintenanceTask'
import { useEventTypeCatalog } from '@/hooks/useEventTypes'
import type { StartEventPayload } from '@/api/equipmentApi'
import { useAuth } from '@/providers/AuthProvider'
import { mintSigningToken } from '@/lib/signingToken'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { SignatureConfirmPanel } from '@/components/renderer/process/SignatureConfirmPanel'

function fmtDate(iso?: string): string {
  if (!iso) return '—'
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  })
}

function nowStamp(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getMonth() + 1)} / ${pad(d.getDate())} / ${d.getFullYear()}  ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function ReadonlyField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className='space-y-1'>
      <p className='text-xs font-medium text-gray-500'>{label}</p>
      <div className='rounded-md border border-gray-200 bg-gray-50/70 px-3 py-2 text-sm text-gray-800'>
        {children}
      </div>
    </div>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className='text-[11px] font-semibold uppercase tracking-wide text-gray-400'>{children}</p>
  )
}

export function StartEventModal({
  task,
  holdsStock = null,
  onClose,
  onConfirm,
}: {
  task: MaintenanceTask | null
  /** What the unit still holds, when Start was already refused for it: ask for the reason up front. */
  holdsStock?: string | null
  onClose: () => void
  /** Rejects with the server's refusal, which the dialog shows. */
  onConfirm: (id: string, payload: StartEventPayload) => Promise<void>
}) {
  const { copy } = useEquipmentScope()
  // The tenant's name for the type, never its code (z8nrz7fb9k).
  const catalog = useEventTypeCatalog()
  const evt = task ? eventMeta(task.eventType, catalog) : null
  const cls = task ? TASK_CLASS_META[task.taskClass] : null
  const checklist = task?.checklist ?? []

  const { email, user } = useAuth()
  const signer = email ?? '' // credential identity — used for verify + audit
  const signerName = user?.fullName || email || '' // display name (never the id/username)

  const [checked, setChecked] = useState<Record<string, boolean>>({})
  const [comments, setComments] = useState('')
  // The password that IS the signature, exchanged for a single-use token the
  // server spends. See lib/signingToken.ts.
  const [password, setPassword] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [signError, setSignError] = useState<string | null>(null)
  // A storage unit that still holds stock starts only with a reason on the
  // record (the server's 409 says what it holds). Shown once that is known.
  const [stockNote, setStockNote] = useState<string | null>(null)
  const [overrideReason, setOverrideReason] = useState('')
  const startTime = useMemo(() => nowStamp(), [task])

  useEffect(() => {
    if (!task) return
    // Every item starts unchecked: the person starting the task confirms each
    // one, and Start waits for the required ones.
    setChecked({})
    setComments('')
    setPassword('')
    setSignError(null)
    setVerifying(false)
    setStockNote(holdsStock)
    setOverrideReason('')
  }, [task, holdsStock])

  // The password is verified by the server as the token is minted, and the token
  // is spent as the transition is applied. Minting first means a wrong password
  // never reaches the transition at all. Each attempt mints its own: a refused
  // Start has already spent the last one.
  const handleConfirm = async () => {
    if (!task || !canStart || verifying) return
    setSignError(null)
    setVerifying(true)
    try {
      let signingToken: string
      try {
        signingToken = await mintSigningToken(password)
      } catch (err) {
        setSignError(apiErrorMessage(err, 'Password could not be verified.'))
        return
      }
      try {
        await onConfirm(task.id, {
          signingToken,
          signatureMeaning: 'Performed by',
          comments: comments.trim() || undefined,
          checklist: checklist.map((c) => ({ itemId: c.id, label: c.label, checked: !!checked[c.id] })),
          evacuationOverrideReason: stockNote ? overrideReason.trim() : undefined,
        })
      } catch (err) {
        // The unit still holds stock: say what, and ask why it starts anyway.
        if ((err as { status?: number }).status === 409 && !stockNote) {
          setStockNote(apiErrorMessage(err, 'This unit still holds stock.'))
        } else {
          setSignError(apiErrorMessage(err, 'This task could not be started.'))
        }
      }
    } finally {
      setVerifying(false)
    }
  }

  const requiredMet = checklist.filter((c) => c.required).every((c) => checked[c.id])
  // Whether the password is CORRECT is the server's answer, not a client-side
  // check — the button only gates on the checklist and something being typed.
  const canStart = requiredMet && signer !== '' && password !== '' && (!stockNote || overrideReason.trim() !== '')

  return (
    <Dialog open={task !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='max-h-[92vh] gap-0 overflow-y-auto p-0 sm:max-w-3xl'>
        {task && evt && cls && (
          <>
            {/* Header */}
            <div className='flex items-start justify-between gap-3 border-b border-gray-100 bg-gray-50/60 px-6 py-4'>
              <div className='flex items-start gap-3'>
                <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary'>
                  <Play className='h-5 w-5 fill-current' />
                </div>
                <div>
                  <h2 className='text-lg font-semibold tracking-tight text-gray-900'>
                    Start{' '}
                    {task.taskClass === 'preventive'
                      ? 'Scheduled PM'
                      : task.taskClass === 'inventory'
                        ? 'Inventory Task'
                        : 'Corrective Event'}
                  </h2>
                  <p className='mt-0.5 text-xs text-muted-foreground'>
                    Confirm details and record actual start
                  </p>
                </div>
              </div>
              <div className='flex items-center gap-2'>
                <span
                  className='inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium'
                  style={{ backgroundColor: `${evt.color}14`, color: evt.color }}
                >
                  <span className='h-1.5 w-1.5 rounded-full' style={{ backgroundColor: evt.color }} />
                  {evt.label.replace(/ \(.*\)$/, '')}
                </span>
                <span className='rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700'>
                  {task.equipmentId}
                </span>
              </div>
            </div>

            {/* Body — two columns */}
            <div className='grid gap-x-8 gap-y-4 px-6 py-5 md:grid-cols-2'>
              {/* Event context */}
              <div className='space-y-3'>
                <SectionLabel>Event context</SectionLabel>
                <ReadonlyField label={copy.unitId}>{task.equipmentId}</ReadonlyField>
                <ReadonlyField label='Event Type'>
                  <span className='inline-flex items-center gap-2'>
                    {evt.label}
                    <span className='h-2 w-2 rounded-full' style={{ backgroundColor: evt.color }} />
                  </span>
                </ReadonlyField>
                <div className='grid grid-cols-2 gap-3'>
                  <ReadonlyField label='Early Start'>{fmtDate(task.earlyStart)}</ReadonlyField>
                  <ReadonlyField label='Due Date'>{fmtDate(task.dueDate)}</ReadonlyField>
                </div>
                <div className='grid grid-cols-2 gap-3'>
                  <ReadonlyField label='Planned Hrs'>{task.planHours.toFixed(1)}</ReadonlyField>
                  <ReadonlyField label='PRT (priority)'>{task.priority ?? '—'}</ReadonlyField>
                </div>
                <div className='space-y-1'>
                  <p className='text-xs font-medium text-gray-500'>Procedure / SOP</p>
                  <div
                    className={cn(
                      'flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold',
                      task.sopRef
                        ? 'border-blue-100 bg-blue-50/60 text-blue-700'
                        : 'border-gray-200 bg-gray-50/70 text-gray-400',
                    )}
                  >
                    <FileText className='h-4 w-4 shrink-0' />
                    {task.sopRef ?? 'No SOP linked'}
                  </div>
                </div>
              </div>

              {/* Execution */}
              <div className='space-y-3'>
                <SectionLabel>Execution</SectionLabel>
                <div className='space-y-1'>
                  <p className='text-xs font-medium text-gray-500'>Actual Start Date &amp; Time</p>
                  <div className='flex items-center justify-between rounded-md border-2 border-primary/40 bg-white px-3 py-2'>
                    <span className='text-sm font-semibold tabular-nums text-gray-900'>{startTime}</span>
                    <span className='rounded-md bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-600'>
                      auto · now
                    </span>
                  </div>
                </div>
                <ReadonlyField label='Performed By'>{signerName || 'not signed in'}</ReadonlyField>

                <div className='space-y-2'>
                  <p className='text-xs font-medium text-gray-500'>Pre-start checklist</p>
                  {checklist.length === 0 && (
                    <p className='text-xs italic text-gray-400'>
                      {task.material
                        ? 'A material has no pre-start checklist.'
                        : `No checklist configured for this ${copy.unitLower} type.`}
                    </p>
                  )}
                  {checklist.map((item) => {
                      const on = !!checked[item.id]
                      return (
                        <button
                          key={item.id}
                          type='button'
                          onClick={() => setChecked((p) => ({ ...p, [item.id]: !p[item.id] }))}
                          className='flex w-full items-center gap-2.5 text-left text-sm'
                        >
                          <span
                            className={cn(
                              'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                              on
                                ? 'border-transparent bg-green-600 text-white'
                                : 'border-gray-300 bg-white',
                            )}
                          >
                            {on && <Check className='h-3 w-3' strokeWidth={3} />}
                          </span>
                          <span className={cn(item.required ? 'text-gray-700' : 'text-gray-400')}>
                            {item.label}
                            {!item.required && ' (optional)'}
                          </span>
                        </button>
                      )
                  })}
                </div>

                {stockNote && (
                  <div className='space-y-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2' role='alert'>
                    <p className='flex items-start gap-1.5 text-xs font-medium text-amber-800'>
                      <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
                      {stockNote}
                    </p>
                    <label className='block space-y-1'>
                      <span className='text-xs font-medium text-gray-600'>Override reason (required)</span>
                      <textarea
                        value={overrideReason}
                        onChange={(e) => setOverrideReason(e.target.value)}
                        rows={2}
                        maxLength={500}
                        placeholder='Why maintenance starts with stock still inside…'
                        className='w-full resize-none rounded-md border border-amber-200 bg-white px-3 py-2 text-sm outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-300'
                      />
                    </label>
                  </div>
                )}

                <div className='space-y-1'>
                  <p className='text-xs font-medium text-gray-500'>Comments (optional)</p>
                  <textarea
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    rows={2}
                    placeholder={copy.startPlaceholder}
                    className='w-full resize-none rounded-md border border-gray-200 px-3 py-2 text-sm outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30'
                  />
                </div>
              </div>
            </div>

            {/* E-signature — the same panel the process-step dialogs use, so one
                signature looks and behaves like every other one. */}
            <div className='border-t border-gray-100 bg-gray-50/60 px-6 py-4'>
              <SignatureConfirmPanel
                meaning='Performed by'
                signerName={signerName}
                value={password}
                onChange={setPassword}
                onSubmit={() => void handleConfirm()}
              />

              <div className='mt-3 flex flex-wrap items-center gap-3'>
                <button
                  type='button'
                  onClick={onClose}
                  className='rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50'
                >
                  Cancel
                </button>
                <p className={cn('text-[11px]', signError ? 'font-medium text-red-600' : 'text-gray-400')}>
                  {signError ?? 'Status Scheduled → Started · actual start written to audit trail'}
                </p>
                <button
                  type='button'
                  disabled={!canStart || verifying}
                  onClick={handleConfirm}
                  className='ml-auto inline-flex items-center gap-2 rounded-md bg-green-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none'
                >
                  <Play className='h-4 w-4 fill-current' />
                  {verifying ? 'Verifying…' : 'Start Event'}
                </button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
